/**
 * ZSM-1 模型类型定义
 */

export const SCHOOL_NAMES = [
  '邹城一中',
  '邹城二中',
  '实验中学',
  '兖矿一中',
  '孟子湖中学'
] as const

export type SchoolName = typeof SCHOOL_NAMES[number]

export interface BandSegment {
  upper: number
  lower: number
  people: number
  recommend_used_yz1: number
}

export interface YearPolicy {
  year: number
  source: string
  source_url: string
  verified: {
    policy_lines: boolean
    ratios: boolean
    segments: boolean
    notes: string
  }
  qualification_line: number | null
  top300_line: number | null
  school_ratios: Record<SchoolName, number | null>
  segments: BandSegment[]
  footnote: string
}

export interface PreferenceRow {
  rank_min: number
  rank_max: number
  label: string
  ratios: Record<SchoolName, number>
  evidence: string
}

export interface PreferenceMatrix {
  version: string
  type: 'PreferenceMatrix'
  description: string
  notes: string[]
  matrix: PreferenceRow[]
  calibration?: unknown
}

export interface NarrativeLibrary {
  version: string
  type: 'NarrativeLibrary'
  description: string
  notes: string[]
  school_global: Record<SchoolName, string>
  specific: Array<{
    rank_min: number
    rank_max: number
    school: SchoolName
    text: string
  }>
}

/** 考生位置（band + 顺位） */
export interface CandidatePosition {
  inTop300: boolean
  band: BandSegment | null
  /** 段内顺位：1 = 段内最高分；超出 [1,10] 表示段宽不足 10 分时的边界 */
  positionInBand: number
  /** 段内累计位置：[0,1]，0 = 段内最前，1 = 段内最后 */
  cumulativePosition: number
  /** 估算的全市名次区间（用于查偏好矩阵） */
  estimatedRankRange: { min: number; max: number }
}

/** 单个学校的 6 项专业指标 */
export interface SchoolMetrics {
  /** 满额预警线 = capacity / demand，∈ [0,1]，达到该比例时段内名额耗尽 */
  burnoutPoint: number
  /** 报名热度 = 预测报名比例 / 官方录取比例 */
  heatPremium: number
  /** 捡漏指数 = 官方比例 / 预测报名比例（等于 1/heatPremium） */
  bargainIndex: number
  /** 段内压强 = 段内人数 / 10（每分平均挤多少人） */
  perScorePressure: number
  /** 顺位安全垫 = burnoutPoint - 段内累计位置 */
  safetyMargin: number
}

/** 三种情境推演的概率 */
export interface ScenarioProbabilities {
  pessimistic: number
  neutral: number
  optimistic: number
}

/** 单个学校的概率结果 */
export interface SchoolProbability {
  school: SchoolName
  capacity: number
  demand: number
  demandBefore: number
  remaining: number
  /** 点估计概率 ∈ [0,1] */
  point: number
  /** 区间下界（第 20 百分位） */
  lower: number
  /** 区间上界（第 80 百分位） */
  upper: number
  /** 冲/稳/保标签 */
  tag: ProbabilityTag
  /** 情绪文案（可空） */
  narrative: string | null
  /** 6 项专业指标 */
  metrics: SchoolMetrics
  /** 三种情境推演 */
  scenarios: ScenarioProbabilities
  /**
   * 结构性确定录取：前方总人数 < 该校段内名额，即「哪怕前方所有人都把该校
   * 填第一志愿也仍有名额」。此时录取与报考偏好估计无关，可安全显示 100%。
   * 反之（前方人数 ≥ 名额）即便点估计算到 ~100%，也只是建立在「前方很少人报
   * 该校」的先验偏好假设上，不应展示裸 100%，UI 封顶 99%「极高概率」。
   * 前 300 直录（规则性）亦置 true。
   */
  structuralCertain: boolean
  /**
   * 点估计被抬到展示下限（模型原值更低，≤1%）。
   * UI 据此提示：下限随预测人群报考热度动态校准，不代表绝对不可能。
   */
  floored?: boolean
}

export type ProbabilityTag = 'safe' | 'recommended' | 'reach' | 'risky' | 'unlikely'

export type ConservativeLevel = 'aggressive' | 'normal' | 'conservative'

/** 计算输入 */
export interface CalcInput {
  score: number
  policy: YearPolicy
  preference: PreferenceMatrix
  narrative?: NarrativeLibrary
  /** 各校热度乘性扰动（默认 1.0），可由 UI 滑块控制 */
  heatAdjust?: Partial<Record<SchoolName, number>>
  /** 保守度（影响冲/稳/保阈值） */
  conservativeLevel?: ConservativeLevel
  /** 蒙特卡洛采样次数（默认 200） */
  monteCarloIterations?: number
  /** 偏好矩阵单值扰动幅度（默认 ±10%） */
  monteCarloPerturbation?: number
  /** 蒙特卡洛随机种子；省略时按输入派生，保证「同输入同结果」。显式传入可固定结果（测试用） */
  seed?: number
  /** 密度重建方法（默认 whittaker，ZSM-1.1 新增） */
  densityMethod?: 'uniform' | 'pchip' | 'whittaker'
  /** 用户测算前选择的"想去的学校"（intent，用于个性化排序） */
  intentSchool?: SchoolName | null
  /**
   * 概率口径（ZSM-1.2 新增）：
   *  - 'ratio'   （默认）旧"剩余名额 ÷ 剩余竞争"压力比模型
   *  - 'bayesian' Dirichlet 先验 + 二项指标真概率（点估计=边际录取概率，区间=偏好不确定性散布）
   */
  probabilityModel?: 'ratio' | 'bayesian'
  /** 贝叶斯模型的 Dirichlet 先验强度（伪计数 K，越小区间越宽）；默认 50 */
  priorStrength?: number
}

/** 计算完整结果 */
export interface CalcResult {
  input: CalcInput
  position: CandidatePosition
  schools: SchoolProbability[]
  /** 已选填的志愿建议（可空） */
  suggestion: {
    first?: SchoolName
    second?: SchoolName
    rationale: string
  }
}
